import { Component, OnInit, Input, ElementRef, Output, EventEmitter, ViewChild, OnDestroy, ChangeDetectionStrategy, ChangeDetectorRef, signal, computed, ViewContainerRef, TemplateRef, HostListener, ViewChildren, QueryList, Directive, AfterViewInit } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { MatDialog } from '@angular/material/dialog';
import { Overlay, OverlayRef, OverlayModule } from '@angular/cdk/overlay';
import { TemplatePortal, PortalModule } from '@angular/cdk/portal';
import { ActiveDescendantKeyManager, Highlightable } from '@angular/cdk/a11y';
import { fromEvent, Subject, Subscription, timer, of } from 'rxjs';
import { catchError, distinctUntilChanged, filter, map, switchMap, take, takeUntil, tap } from 'rxjs/operators';
import { PartnersService } from '@app/common/partners/partners.service';
import { AlertService } from '@app/shared/alert.service';
import { LoginService } from '@app/common/login.service';
import { PartnersComponent } from '@app/common/partners/partners.component';
import { PARTNER_ROLE_INTERNAL, PARTNER_ROLE_MANUFACTURER, PARTNER_ROLE_SUPPLIER, PARTNER_ROLE_CUSTOMER } from '@app/shared/constants';
// PAS d'import de la fiche tiers (PartnerComponent) ici : elle importe ce sélecteur (colonne
// « Relations »), et un import statique dans les deux sens ferait évaluer l'un des deux
// décorateurs avant que l'autre classe existe (« Cannot access … before initialization »).
// La fiche est chargée à la demande, dans openPartnerDialog — voir là-bas.
import * as util from "@app/shared/util";
import { Partner } from '../_models/common';


// ViewModel pour les options de partenaires
export interface PartnerViewModel {
  label: string;
  subLabel: string;
  raw: any;
}

/**
 * DIRECTIVE INTERNE POUR LES OPTIONS DE PARTENAIRES
 */
@Directive({
  selector: '[partnerOption]',
  standalone: true,
  exportAs: 'partnerOption'
})
export class PartnerOptionDirective implements Highlightable {
  @Input({ required: true }) item: any;
  private _isActive = signal(false);
  isActive = this._isActive.asReadonly();
  constructor(public element: ElementRef) { }
  setActiveStyles(): void {
    this._isActive.set(true);
    this.element.nativeElement.scrollIntoView({ block: 'nearest', inline: 'nearest' });
  }
  setInactiveStyles(): void {
    this._isActive.set(false);
  }
  getLabel(): string {
    return this.element.nativeElement.textContent.trim();
  }
}

/**
 * COMPOSANT SEARCH-PARTNER UTILISANT LA LOGIQUE SMART-SELECT
 */
@Component({
  selector: 'app-search-partner',
  standalone: true,
  templateUrl: './search-partner.component.html',
  styleUrls: ['./search-partner.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [FormsModule, OverlayModule, PortalModule, PartnerOptionDirective]
})
export class SearchPartnerComponent implements OnInit, AfterViewInit, OnDestroy {
  // --- INPUTS ---
  @Input() resetComponentSubject: Subject<boolean> = new Subject<boolean>();
  @Input() currentPartner: Partner | undefined | null;
  @Input() roles: number[] = [PARTNER_ROLE_CUSTOMER]; // Défaut : Client. Peut être Interne, Fabricant, Fournisseur, Client (constants.PARTNER_ROLE_*, c_values 2)
  @Input() width: string = '100%';  // Largeur du wrapper (ex: '100%', '250px', 'auto')
  @Input() showSelectionInInput: boolean = false;  // lorsque l on a selectionner un partener alors celui ci s ecrit dans l input avec un lien vers sa fiche (permet de gagner de la place ) il faudra alors appuyer sur la croix pour libérer a nouveau l'input
  @Input() canEdit: boolean = true;


  // --- OUTPUTS ---
  @Output() partnerChange = new EventEmitter<any>();
  /** Le tiers AFFICHÉ vient d'être modifié dans sa fiche — ce n'est pas un choix : le parent
   *  relit son tiers sans rejouer ce qu'il fait d'un partnerChange (adresses par défaut…). */
  @Output() partnerUpdated = new EventEmitter<any>();

  // --- VIEW CHILDREN ---
  @ViewChild('searchInput') searchInputRef!: ElementRef<HTMLInputElement>;
  @ViewChild('trigger') triggerElement!: ElementRef<HTMLElement>;
  @ViewChild('panel') panelTemplate!: TemplateRef<any>;
  @ViewChild('errorPanel') errorPanelTemplate!: TemplateRef<any>;
  @ViewChildren(PartnerOptionDirective) options!: QueryList<PartnerOptionDirective>;

  // --- CONFIGURATION ---
  private readonly debounceTime = 300;
  public readonly placeholders: { [key: number]: string } = {
    [PARTNER_ROLE_INTERNAL]: "Recherche interne", [PARTNER_ROLE_MANUFACTURER]: "Nom du Fabriquant",
    [PARTNER_ROLE_SUPPLIER]: "Nom du Fournisseur", [PARTNER_ROLE_CUSTOMER]: "Rechercher par nom / prénom / NIR",
  };


  // --- ÉTAT RÉACTIF (Signals) ---
  readonly searchValue = signal<string>('');
  readonly isOpen = signal(false);
  readonly isLoading = signal(false);
  readonly notFound = signal(false);
  private readonly _apiResults = signal<any[]>([]);
  readonly isFocused = signal<boolean>(false);

  // --- COMPUTED ---
  readonly viewModels = computed<PartnerViewModel[]>(() => {
    return this._apiResults().map(partner => ({
      label: this.formatPartnerName(partner),
      subLabel: this.formatPartnerCity(partner),
      raw: partner
    }));
  });

  readonly hasResults = computed(() => this._apiResults().length > 0);
  readonly placeholder = computed(() => this.placeholders[this.roles[0]] || this.placeholders[PARTNER_ROLE_CUSTOMER]);
  readonly roleLabel = computed(() => {
    if (this.roles.includes(PARTNER_ROLE_MANUFACTURER)) return 'Fabricant';
    if (this.roles.includes(PARTNER_ROLE_SUPPLIER)) return 'Fournisseur';
    return 'Client';
  });

  // --- GESTION INTERNE ---
  private keyManager!: ActiveDescendantKeyManager<PartnerOptionDirective>;
  private overlayRef: OverlayRef | null = null;
  private errorOverlayRef: OverlayRef | null = null;
  private portal: TemplatePortal<any> | null = null;
  private errorPortal: TemplatePortal<any> | null = null;
  private subscriptions: Subscription[] = [];
  private searchSub: Subscription | null = null;
  private destroy$ = new Subject<void>();
  private destroySearch$ = new Subject<void>();

  constructor(
    public loginService: LoginService,
    private partnersService: PartnersService,
    private alertService: AlertService,
    public dialog: MatDialog,
    private overlay: Overlay,
    private viewContainerRef: ViewContainerRef,
    private cdr: ChangeDetectorRef
  ) { }

  ngOnInit(): void {
    console.log('this.curentPartner', this.currentPartner);
    if (this.currentPartner && this.currentPartner.name) {
      this.searchValue.set(this.currentPartner.name)
    }
  }

  ngAfterViewInit(): void {
    // For Dev
    // setTimeout(() => {
    //   this.searchValue.set('jardin du sud');
    //   this.partnersService.getPartnersByFilter(
    //     util.postgressearchstring('jardin du sud'),
    //     undefined, undefined, undefined, undefined,
    //     this.roles
    //   ).pipe(take(1)).subscribe(results => {
    //     if (results.length > 0) {
    //       this.selectPartner(results[0]);
    //     }
    //   });
    // }, 1500);

    // Initialisation du KeyManager pour la navigation clavier
    this.keyManager = new ActiveDescendantKeyManager(this.options)
      .withWrap()
      .withVerticalOrientation()
      .withTypeAhead();

    // Mise à jour du KeyManager quand les options changent
    this.options.changes.pipe(takeUntil(this.destroy$)).subscribe(() => {
      if (this.isOpen() && this.options.length > 0) {
        setTimeout(() => this.keyManager.setFirstItemActive(), 0);
      }
    });

    // Configuration de la recherche sur l'input
    this.setupSearchObservable();

    // Souscription au reset externe
    this.subscriptions.push(
      this.resetComponentSubject.pipe(takeUntil(this.destroy$)).subscribe(shouldReset => {
        if (shouldReset) {
          this.resetPartner();
        }
      })
    );
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
    this.destroySearch$.next();
    this.destroySearch$.complete();
    this.overlayRef?.dispose();
    this.errorOverlayRef?.dispose();
    this.subscriptions.forEach(sub => sub?.unsubscribe());
  }

  // --- CONFIGURATION DE LA RECHERCHE ---

  private setupSearchObservable(): void {
    const trigger = this.triggerElement?.nativeElement;
    if (!trigger) return;

    // Un seul pipeline vivant : resetPartner rappelle cette méthode (parfois dès le démarrage, la
    // commande émettant son reset), et sans cette coupure chaque frappe partait en deux requêtes.
    this.searchSub?.unsubscribe();

    // Réinitialiser destroySearch$ pour permettre de nouvelles recherches
    this.destroySearch$ = new Subject<void>();

    // Le conteneur stable, pas l'<input> : il est recréé à chaque sortie du mode lien, même quand le parent vide le tiers sans reset.
    this.searchSub = fromEvent<KeyboardEvent>(trigger, 'keyup').pipe(
      takeUntil(this.destroy$),
      // Filtre Enter pour sélection directe
      filter((event: KeyboardEvent) => {
        if (!(event.target instanceof HTMLInputElement)) return false;
        if (event.key === 'Enter') {
          this.handleEnterKey();
          return false;
        }
        return true;
      }),
      // Ignore les flèches (gérées par HostListener)
      filter((event: KeyboardEvent) => !['ArrowLeft', 'ArrowUp', 'ArrowRight', 'ArrowDown'].includes(event.key)),
      map(() => this.searchValue().trim()),
      distinctUntilChanged(),
      // Nettoyage APRÈS le filtre de valeur : Maj, Échap ou Fin ne vident plus la liste ouverte.
      tap(() => {
        this.notFound.set(false);
        this._apiResults.set([]);
        this.close();
        this.closeError();
      }),
      // Filtre sur la longueur et le type
      filter((value: string) => {
        if (value.length < 3) {
          this.isLoading.set(false);
          this._apiResults.set([]);
          this.close();
          this.closeError();
          this.destroySearch$.next();
          return false;
        }
        // Si c'est un nombre (potentiel NIR), on ne cherche pas
        if (!isNaN(+value)) {
          this._apiResults.set([]);
          this.closeError();
          this.isLoading.set(false);
          this.destroySearch$.next(); // la réponse du nom tapé avant ce NIR rouvrirait sa liste
          return false;
        }
        this.closeError(); // Ferme l'erreur quand on commence une nouvelle recherche
        this.isLoading.set(true);
        return true;
      }),
      // Debounce avec possibilité d'annulation
      switchMap((searchValue: string) =>
        timer(this.debounceTime).pipe(
          takeUntil(this.destroySearch$),
          map(() => searchValue)
        )
      ),
      // Appel API
      switchMap((searchValue: string) => {
        this.isLoading.set(true);
        this._apiResults.set([]);
        return this.partnersService.getPartnersByFilter(
          util.postgressearchstring(searchValue),
          undefined, undefined, undefined, undefined,
          this.roles
        ).pipe(
          takeUntil(this.destroySearch$),
          catchError(err => {
            this.isLoading.set(false);
            console.error('Erreur recherche partenaire:', err);
            return of([]);
          })
        );
      })
    ).subscribe({
      next: (results: any[]) => {
        this._apiResults.set(results);
        this.isLoading.set(false);
        if (results.length === 0) {
          this.notFound.set(true);
          this.openError();
        } else {
          this.open();
        }
        this.searchInputRef?.nativeElement?.focus();
      },
      error: (err) => {
        console.error('ERREUR Serveur:', err);
        this.isLoading.set(false);
        this._apiResults.set([]);
        this.alertService.error('ERREUR Serveur: ' + (err.error || err.message));
      }
    });
  }

  private handleEnterKey(): void {
    this.isLoading.set(false);
    this.destroySearch$.next();
    if (this.isOpen() && this.keyManager.activeItem) {
      // Sélectionne l'item actif
      console.log('this.keyManager.activeItem.item', this.keyManager.activeItem.item);
      this.selectPartner(this.keyManager.activeItem.item);
    } else if (this.searchValue().trim()) {
      // pas de sens je ne sais pas pourquoi il y a ceci ici TODO remove it
      // Pas de résultats visibles, on prend le texte brut
      console.log('his.searchValue().trim()', this.searchValue().trim());
      // this.selectPartner(this.searchValue().trim());
    }
    this._apiResults.set([]);
    this.close();
  }

  // --- GESTION CLAVIER (HostListener) ---

  @HostListener('keydown', ['$event'])
  onKeydown(event: KeyboardEvent): void {
    if (!this.isOpen()) {
      if (event.key === 'ArrowDown') {
        event.preventDefault();
        if (this.hasResults()) {
          this.open();
        }
      }
      return;
    }
    switch (event.key) {
      case 'Enter':
        event.preventDefault();
        if (this.keyManager.activeItem) {
          this.selectPartner(this.keyManager.activeItem.item);
        }
        break;
      case 'Escape':
      case 'Tab':
        this.close();
        break;
      case 'ArrowUp':
      case 'ArrowDown':
        event.preventDefault();
        this.keyManager.onKeydown(event);
        break;
      default:
        this.keyManager.onKeydown(event);
    }
  }

  // --- GESTION OVERLAY ---

  private getInputWidth(): number {
    // Utilise la largeur de l'input wrapper (qui contient l'input + les boutons)
    const inputWrapper = this.triggerElement.nativeElement.querySelector('.input-wrapper') as HTMLElement;
    return inputWrapper?.getBoundingClientRect().width ?? this.triggerElement.nativeElement.getBoundingClientRect().width;
  }

  private createOverlay(): void {
    if (this.overlayRef) return;
    const positionStrategy = this.overlay.position()
      .flexibleConnectedTo(this.triggerElement)
      .withPositions([
        { originX: 'start', originY: 'bottom', overlayX: 'start', overlayY: 'top', offsetY: 4 },
        { originX: 'start', originY: 'top', overlayX: 'start', overlayY: 'bottom', offsetY: -4 }
      ])
      .withFlexibleDimensions(false)
      .withPush(false);
    this.overlayRef = this.overlay.create({
      positionStrategy,
      scrollStrategy: this.overlay.scrollStrategies.reposition(),
      hasBackdrop: false
    });
  }

  private createErrorOverlay(): void {
    if (this.errorOverlayRef) return;
    const positionStrategy = this.overlay.position()
      .flexibleConnectedTo(this.triggerElement)
      .withPositions([
        { originX: 'start', originY: 'bottom', overlayX: 'start', overlayY: 'top', offsetY: 4 },
        { originX: 'start', originY: 'top', overlayX: 'start', overlayY: 'bottom', offsetY: -4 }
      ])
      .withFlexibleDimensions(false)
      .withPush(false);
    this.errorOverlayRef = this.overlay.create({
      positionStrategy,
      scrollStrategy: this.overlay.scrollStrategies.reposition(),
      hasBackdrop: false
    });
  }

  open(): void {
    if (this.isOpen() || !this.hasResults()) return;
    this.closeError(); // Ferme l'erreur si ouverte
    this.createOverlay();
    // Ajuste la largeur à l'input
    const inputWidth = this.getInputWidth();
    if (this.overlayRef) {
      this.overlayRef.updateSize({ width: inputWidth });
    }
    this.isOpen.set(true);
    if (this.overlayRef && !this.overlayRef.hasAttached()) {
      this.portal = new TemplatePortal(this.panelTemplate, this.viewContainerRef);
      this.overlayRef.attach(this.portal);
    }
    // Fermeture sur clic extérieur
    this.overlayRef!.outsidePointerEvents()
      .pipe(takeUntil(this.destroy$))
      .subscribe((event: MouseEvent) => {
        const target = event.target as HTMLElement;
        const trigger = this.triggerElement.nativeElement;
        if (!trigger.contains(target)) {
          this.close();
        }
      });
    // Active le premier item
    setTimeout(() => this.keyManager.setFirstItemActive(), 0);
  }

  close(): void {
    if (!this.isOpen()) return;
    this.isOpen.set(false);
    this.overlayRef?.detach();
  }

  openError(): void {
    if (this.errorOverlayRef?.hasAttached()) return;
    this.close(); // Ferme les résultats si ouverts
    this.createErrorOverlay();
    // Ajuste la largeur à l'input
    const inputWidth = this.getInputWidth();
    if (this.errorOverlayRef) {
      this.errorOverlayRef.updateSize({ width: inputWidth });
    }
    if (this.errorOverlayRef && !this.errorOverlayRef.hasAttached()) {
      this.errorPortal = new TemplatePortal(this.errorPanelTemplate, this.viewContainerRef);
      this.errorOverlayRef.attach(this.errorPortal);
    }
    // Fermeture sur clic extérieur
    this.errorOverlayRef!.outsidePointerEvents()
      .pipe(takeUntil(this.destroy$))
      .subscribe((event: MouseEvent) => {
        const target = event.target as HTMLElement;
        const trigger = this.triggerElement.nativeElement;
        if (!trigger.contains(target)) {
          this.closeError();
        }
      });
  }

  closeError(): void {
    this.notFound.set(false);
    this.errorOverlayRef?.detach();
  }

  // --- FORMATAGE ---

  formatPartnerName(partner: any): string {
    if (!partner) return '';
    let name = '';
    if (partner.name) name = partner.name;
    if (partner.firstname) name += ' ' + partner.firstname;
    return name.trim();
  }

  formatPartnerCity(partner: any): string {
    // L'adresse de facturation par défaut (ligne de rôle -1 de c_partner_partner), sinon la première
    const addr: any = Partner.mainAddress(partner);
    if (!addr) return '';
    if (addr.city && addr.city.trim() !== '') {
      return addr.city.trim().substring(0, 9);
    }
    return '';
  }

  // --- ACTIONS ---

  onOptionClick(partner: any): void {
    this.selectPartner(partner);
  }

  selectPartner(partner: any): void {
    if (!partner) return;
    // Le choix est fait : une recherche encore en vol rouvrirait la liste à sa réponse.
    this.isLoading.set(false);
    this.destroySearch$.next();
    this.partnerChange.emit(partner);
    this.searchValue.set('');
    this._apiResults.set([]);
    this.close();
    this.closeError();
    setTimeout(() => this.searchInputRef?.nativeElement?.focus(), 0);
  }

  resetPartner(inputValue?: string): void {
    // inputValue peut être utilisé si besoin (ex: logging, analytics)
    console.log('Reset avec valeur:', inputValue);
    this.searchValue.set('');
    this._apiResults.set([]);
    this.isLoading.set(false);
    this.destroySearch$.next();
    this.close();
    this.closeError();
    this.partnerChange.emit(undefined);

    // Reconfigurer l'observable de recherche après un court délai pour que l'input soit bien dans le DOM
    setTimeout(() => {
      this.setupSearchObservable();
      this.searchInputRef?.nativeElement?.focus();
    }, 0);
  }

  /** La loupe rend le tiers CHOISI (ligne cliquée) ou CRÉÉ sur place ; sans cette souscription il
   *  mourait à la fermeture. `selectPartner` = le chemin exact d'un clic dans les résultats. */
  openPartnersDialog(inputValue?: string): void {
    const searchCriteria = (inputValue ?? this.searchValue()).trim();
    const dialogRef = this.dialog.open(PartnersComponent, {
      width: '80%',
      height: '80%',
      data: {
        mode: 'DIALOG',
        searchcriteria: searchCriteria,
        roles: [...this.roles],
      }
    });
    dialogRef.afterClosed().pipe(take(1)).subscribe((p: any) => {
      if (!p?.id) return;
      // Fabricant et fournisseur s'enregistrent sur l'article dès le choix : un tiers sans ce rôle (créé « Client » par défaut) est refusé.
      const strict: number[] = this.roles.filter(r => r === PARTNER_ROLE_MANUFACTURER || r === PARTNER_ROLE_SUPPLIER);
      if (strict.length && !(p.roles ?? []).some((r: any) => strict.includes(+r))) {
        const role = strict.includes(PARTNER_ROLE_MANUFACTURER) ? 'Fabricant' : 'Fournisseur';
        this.alertService.error(`${this.formatPartnerName(p)} n'a pas le rôle ${role} : ajoutez-le dans sa fiche.`);
        return;
      }
      this.selectPartner(p);
    });
  }

  /** Le lien du nom ouvre la fiche du tiers affiché (triplet de fenêtre et import dynamique : voir front.common.partners) ; au retour,
   *  le tiers relu sort par `partnerUpdated`, jamais par `partnerChange` (un choix, qui réaffecterait les adresses de la commande). */
  openPartnerDialog(idpartner: Number) {
    import('@app/common/partner/partner.component').then(({ PartnerComponent }) => {
      const dialogRef = this.dialog.open(PartnerComponent, {
        width: '96%',
        height: '94%',
        maxWidth: '96vw',
        data: { mode: "DIALOG", "id": idpartner }
      });
      dialogRef.afterClosed().pipe(take(1)).subscribe((p: any) => {
        if (!p?.id || !this.currentPartner || +p.id !== +this.currentPartner.id) return;
        this.currentPartner = { ...this.currentPartner, name: p.name, firstname: p.firstname };
        this.cdr.markForCheck();		// OnPush : l'entrée n'a pas changé de référence côté parent
        this.partnerUpdated.emit(p);
      });
    });
  }




  onInputFocus(event: FocusEvent): void {
    this.isFocused.set(true);
    if (this.hasResults()) {
      this.open();
    }
    (event.target as HTMLInputElement).select();
  }

  onInputBlur(): void {
    // Délai pour permettre le clic sur une option
    setTimeout(() => {
      if (!this.overlayRef?.hasAttached()) {
        // Ne ferme pas si on clique sur le panel
      }
    }, 150);
  }

  onButtonFocus(): void {
    // Masque le curseur clignotant de l'input quand un bouton a le focus
    if (this.searchInputRef?.nativeElement) {
      this.searchInputRef.nativeElement.style.caretColor = 'transparent';
    }
  }

  onButtonBlur(): void {
    // Restaure le curseur de l'input quand le bouton perd le focus
    if (this.searchInputRef?.nativeElement) {
      this.searchInputRef.nativeElement.style.caretColor = '';
    }
  }

  // Méthode pour mettre à jour searchValue depuis le template (two-way binding manuel)
  onSearchInput(event: Event): void {
    const value = (event.target as HTMLInputElement).value;
    this.searchValue.set(value);
  }
}
